
export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'disconnecting' | 'error';

/**
 * Processing state for Vezgo connections
 * 
 * Note: This aligns with the simplified connection status approach used in Wealthica v2.
 * See docs/wealthica/rebuild/implementation-v2.md for more details.
 */
export enum ConnectionProcessingState {
  IDLE = 'idle',
  CONNECTING = 'connecting',     // Covers initial connection process
  LOADING_ACCOUNTS = 'loading_accounts',
  REFRESHING = 'refreshing',
  DISCONNECTING = 'disconnecting',
  PROCESSING = 'processing',
  COMPLETE = 'complete',
  FAILED = 'failed',
  ERROR = 'error'               // Generic error state that covers multiple error conditions
}

/**
 * Standard error structure for Vezgo operations
 */
export interface VezgoError extends Error {
  code?: string;
  details?: string;
  type?: string;
  recoverable?: boolean;
  timestamp?: number;
  [key: string]: any;
}

/**
 * Account data structure returned from Vezgo
 */
export interface VezgoAccount {
  id: string;
  name: string;
  type: string;
  balance?: number;
  currency?: string;
  provider?: string;
  lastUpdated?: string | Date;
  [key: string]: any;
}

/**
 * Connection data structure for Vezgo providers
 */
export interface VezgoConnection {
  id: string;
  provider: string;
  provider_name?: string;
  status?: string;
  lastChecked?: Date;
  errorCode?: string;
  errorMessage?: string;
  metadata?: Record<string, any>;
  providerDisplayName?: string;
  user_id?: string;
  created_at?: string;
  updated_at?: string;
  connection_status?: string;
  vezgo_account_id?: string;
}

/**
 * Result of a Vezgo callback processing operation
 */
export interface VezgoCallbackResult {
  success: boolean;
  provider: string;
  error?: any;
}

/**
 * Actions available for connection management
 */
export interface VezgoConnectionActions {
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * State change handler for connection events
 */
export type VezgoStateChangeHandler = (state: { 
  connection?: VezgoConnection; 
  accounts: VezgoAccount[] 
}) => void;

/**
 * Action event handler types
 */
export type VezgoActionStartHandler = (action: string) => void;
export type VezgoActionCompleteHandler = (action: string, success: boolean, data?: any) => void;
